import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

import { Button, type ButtonProps } from '@/components/ui/Button'
import { useDismissable } from '@/hooks/useDismissable'
import { cn } from '@/lib/cn'

export interface MenuProps {
  /** The trigger's text. */
  label: ReactNode
  /** Rendered above the items — a count, a note about what they apply to. */
  header?: ReactNode
  children: (close: () => void) => ReactNode
  align?: 'start' | 'end'
  triggerProps?: Omit<ButtonProps, 'children' | 'onClick'>
  className?: string
}

/**
 * A dropdown menu with the keyboard behaviour the role implies.
 *
 * Focus moves between items with Up/Down/Home/End, Escape closes and returns
 * focus to the trigger, and an outside click dismisses. The items are found by
 * querying `[role="menuitem"]` inside the panel, so a caller can lay them out
 * however it likes — grouped, split into a footer row — without the navigation
 * having to know the structure.
 */
export function Menu({
  label,
  header,
  children,
  align = 'end',
  triggerProps,
  className,
}: MenuProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  // Escape is handled by the document listener inside `useDismissable`, which
  // fires on capture and unmounts the panel before the panel's own handler can
  // run. Without returning focus here, dismissing with the keyboard dropped the
  // caret on <body> and the next Tab restarted at the skip link. An outside
  // click closes without moving focus, since the user has already chosen where
  // it should go.
  const containerRef = useDismissable<HTMLDivElement>({
    open,
    onClose: (reason) => (reason === 'escape' ? close() : setOpen(false)),
    closeOnOutsideClick: true,
  })

  const items = () =>
    Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])

  const move = (delta: number, absolute?: 'first' | 'last') => {
    const list = items()
    if (list.length === 0) return
    if (absolute === 'first') return list[0].focus()
    if (absolute === 'last') return list[list.length - 1].focus()
    const index = list.indexOf(document.activeElement as HTMLElement)
    const next = index < 0 ? 0 : (index + delta + list.length) % list.length
    list[next].focus()
  }

  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        return move(1)
      case 'ArrowUp':
        event.preventDefault()
        return move(-1)
      case 'Home':
        event.preventDefault()
        return move(0, 'first')
      case 'End':
        event.preventDefault()
        return move(0, 'last')
      case 'Escape':
        event.preventDefault()
        return close()
      case 'Tab':
        return setOpen(false)
    }
  }

  const openWith = (focus: 'first' | 'last') => {
    setOpen(true)
    requestAnimationFrame(() => move(0, focus))
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openWith('first'))}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            openWith('first')
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            openWith('last')
          }
        }}
        {...triggerProps}
      >
        {label}
        <svg
          viewBox="0 0 12 12"
          className={cn(
            'h-2.5 w-2.5 opacity-60 transition-transform duration-150 ease-out',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 8l3-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Button>

      {open ? (
        <div
          ref={panelRef}
          role="menu"
          onKeyDown={onPanelKeyDown}
          className={cn(
            'animate-rise absolute top-full z-(--z-dropdown) mt-1.5 w-64 overflow-hidden',
            'rounded-xl border border-line bg-surface-raised shadow-lg',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {header ? (
            <p className="border-b border-line px-3 py-2 text-xs text-ink-muted">{header}</p>
          ) : null}
          {children(close)}
        </div>
      ) : null}
    </div>
  )
}

export interface MenuItemProps {
  onSelect: () => void
  children: ReactNode
  description?: ReactNode
  icon?: ReactNode
  className?: string
}

export function MenuItem({
  onSelect,
  children,
  description,
  icon,
  className,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors duration-100',
        'hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
        className,
      )}
    >
      {icon ? <span className="mt-0.5 shrink-0 text-ink-muted">{icon}</span> : null}
      <span className="min-w-0">
        <span className="block text-base font-medium text-ink">{children}</span>
        {description ? (
          <span className="block text-xs text-ink-muted">{description}</span>
        ) : null}
      </span>
    </button>
  )
}
